import React, { memo, useCallback } from 'react';
import { InjectedFormProps, reduxForm } from 'redux-form';
import { useTranslation } from 'react-i18next';

import { AudienceInput } from '@common/components/form/audience';
import { DialogWrapper } from '@common/components/modal/dialog-wrapper';
import { EPlanPackageConfig } from '@common/definitions';
import { usePlanPackageAudienceHandlers } from '@common/hooks/use-plan-package-audience-handlers';

type AudienceModalProps = {
  close: () => void;
  onSubmit: (data: Record<string, any>) => void;
};

type Props = InjectedFormProps<{}, AudienceModalProps> & AudienceModalProps;

const AudienceModal = memo(({
  close, onSubmit, handleSubmit
}: Props) => {
  const { t } = useTranslation();
  const planPackageAudienceHandlers = usePlanPackageAudienceHandlers({
    packageIdAddFilters: EPlanPackageConfig.SURVEYS_AUDIENCE_SELECTOR,
    packageIdDaysInService: EPlanPackageConfig.SURVEYS_AUDIENCE_SELECTOR_ONBOARDING,
  });

  const submit = useCallback((data) => {
    onSubmit(data);
    close();
  }, [onSubmit, close]);

  return (
    <DialogWrapper
      show
      onHide={close}
      size="large"
      className="BootstrapModal"
      title={t('survey:select_audience')}
      onConfirm={() => handleSubmit(submit)()}
      confirmButtonText={t('core:confirm')}
      confirmButtonType="submit"
      confirmButtonForm="survey-settings-audience"
      cancelButtonText={t('common:modal_footer_button_cancel')}
      content={
        <form id="survey-settings-audience" onSubmit={handleSubmit(submit)}>
          <AudienceInput
            name="audience"
            tooltips={{ audience: t('survey:audience_selector_tooltip') }}
            addContextToPredicatesOnChange
            {...planPackageAudienceHandlers}
          />
        </form>
      }
    />
  );
});

const form = reduxForm<{}, AudienceModalProps >({ form: 'survey-settings-audience' });
const AudienceModalForm = form(AudienceModal);

export default AudienceModalForm;
